import React, { useCallback, useRef, useState } from "react";
import { Breadcrumb, BreadcrumbItem, Button, Col, Container, Nav, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Webcam from "react-webcam";
import { checkLivenessSession, createLivenessSession, UserVerify } from "../../utils/queries";

const fetchUserLocation = async () => {
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject);
    });

    return {
      locLatLong: `${position.coords.latitude},${position.coords.longitude}`,
    };
  } catch (error) {
    console.error("Error getting location:", error);
    toast.error("Failed to get location. Please enable location services.");
    return {
      locLatLong: "",
    };
  }
};

const AdminFaceAuth = () => {
  const { userInfo } = useSelector((state) => state.auth);

  const webcamRef = useRef(null);
  const [submit, setSubmit] = useState(false);

  const userType = userInfo && userInfo[1]?.InfoField2;
  const empCode = userInfo && userInfo[1]?.InfoField;
  const ReqCompanyCode = userInfo && userInfo[13]?.InfoField;

  const getLivenessSession = async () => {
    try {
      const response = await checkLivenessSession();
      const data = response[0];
      if (data.status === 200) {
        return data.InfoField1;
      } else {
        return toast.warn(data.message);
      }
    } catch (error) {
      console.error("Error creating liveness session:", error);
      toast.error("Liveness session failed.");
    }
  };

  const captureVideo = async (duration) => {
    if (!webcamRef.current) return;

    const stream = webcamRef.current.stream;
    const mediaRecorder = new MediaRecorder(stream);
    const chunks = [];

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };

    mediaRecorder.onstop = async () => {
      const blob = new Blob(chunks, { type: "video/webm" });
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = async () => {
        const base64data = reader.result;
        await submitVideo(base64data);
      };
    };

    mediaRecorder.start();
    setTimeout(() => {
      mediaRecorder.stop();
    }, duration);
  };

  const submitVideo = async (videoBlob) => {
    setSubmit(true);
    const sessionId = await getLivenessSession();
    if (!sessionId) return toast.info("Liveness check failed!");

    try {
      const response = await createLivenessSession(videoBlob, sessionId);
      const result = response[0];

      if (result.status === 200) {
        toast.success("Liveness confirmed.");
        await handleIndexFace(result.InfoField1);
      } else {
        toast.info(result.message);
      }
    } catch (error) {
      console.log(error, " error");
      toast.error("Verification error.");
    } finally {
      setSubmit(false);
    }
  };

  const handleIndexFace = useCallback(
    async (faceId) => {
      setSubmit(true);

      try {
        const imageSrc = webcamRef?.current?.getScreenshot();
        if (!imageSrc) {
          toast.info("Failed to capture image. Please try again.");
          setSubmit(false);
          return;
        }

        try {
          const location = await fetchUserLocation();

          const base64Data = imageSrc.replace(/^data:image\/[a-zA-Z]+;base64,/, "");
          const response = await UserVerify(base64Data, faceId, empCode, ReqCompanyCode, location.locLatLong);
          const res = response[0];

          if (res.status === 200) {
            toast.success(res.message);
          } else {
            toast.info(res.message);
          }
        } catch (error) {
          toast.error("Verification failed.");
        } finally {
          setSubmit(false);
        }
      } catch (error) {
        toast.error("Error detecting face. Please try again.");
        setSubmit(false);
      }
    },
    [webcamRef]
  );

  return (
    <>
      {userType === "3" && (
        <Breadcrumb>
          <BreadcrumbItem>
            <Nav.Link as={Link} to="/dashboard" className="text-success text-decoration-none">
              Dashboard
            </Nav.Link>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <Nav.Link as={Link} to="/admin-attendance-management" className="text-success text-decoration-none">
              Attendance Management
            </Nav.Link>
          </BreadcrumbItem>
          <BreadcrumbItem active>Face Authentication</BreadcrumbItem>
        </Breadcrumb>
      )}

      <Container className="my-1">
        <Row className="justify-content-center">
          <Col xs={12} sm={6} md={4} lg={3}>
            <h4 className="text-center">Admin Face Authentication</h4>
          </Col>
        </Row>

        <Row className="justify-content-center">
          <Col xs={12} md={6} className="d-flex flex-column align-items-center gap-2 p-1 border rounded">
            <Webcam ref={webcamRef} screenshotFormat="image/jpeg" className="rounded img-fluid" />

            {!submit && (
              <div className="text-center mt-1">
                <Button variant="dark" type="button" disabled={submit} onClick={() => captureVideo(5000)}>
                  SUBMIT
                </Button>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AdminFaceAuth; 